
import React, { useState, useEffect } from "react";
import { Copy, Gift, Users, Share2, MessageCircle, MessageSquare } from "lucide-react";
import { FaWhatsapp, FaRegStar, FaTelegram, FaLinkedin, FaInstagram } from "react-icons/fa";
import { SiGmail } from "react-icons/si";
import "./Refereran.css";
import axios from "axios";

const ReferEarn = ({ userId }) => {
  const [copied, setCopied] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState("");
  const [selected, setSelected] = useState(null);

  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    earnedAmount: 0,
    profileImage: ""
  });

  // App download link
  const APP_DOWNLOAD_LINK = "https://play.google.com/store/apps/details?id=com.maamaas.app";

  // Fetch auth token from localStorage
  useEffect(() => {
    const token = localStorage.getItem("authToken") || 
                  sessionStorage.getItem("authToken") || 
                  localStorage.getItem("token");
    if (token) {
      setAuthToken(token);
      //console.log("✅ Token found:", token.substring(0, 20) + "...");
    } else {
      console.warn("⚠️ No auth token found");
    }
  }, []);

  const handleSelect = (option) => {
    setSelected(option);
    //console.log("Selected:", option);
  };

  // Fetch referral code and stats from API
  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get user ID from props, localStorage, or use default
        const id = userId || localStorage.getItem("userId") || localStorage.getItem("professionalUserId") || 15;
        //console.log("🔄 Fetching referral data for user ID:", id);

        // Get token from multiple possible locations
        const token = authToken || 
                     localStorage.getItem("authToken") || 
                     sessionStorage.getItem("authToken") ||
                     localStorage.getItem("token");

        if (!token) {
          console.warn("⚠️ No auth token found, using fallback data");
          setReferralCode("MAMA#409");
          setReferralStats({
            totalReferrals: 0,
            earnedAmount: 0,
            profileImage: ""
          });
          setLoading(false);
          return;
        }

        // ✅ FIXED: Correct API URL format for your backend
        // Option 1: Try the profile API first
        try {
          const profileApiUrl = `https://backend.maamaas.com/subscription/api/user/getprofile/${id}`;
          //console.log("🌐 Profile API URL:", profileApiUrl);

          const profileResponse = await axios.get(profileApiUrl, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'auth-token': token
            },
            timeout: 10000 // 10 second timeout
          });

          //console.log("✅ Profile API Response:", profileResponse.data);

          if (profileResponse.data) {
            // Check different possible response formats
            const data = profileResponse.data;
            
            // Try to find referral code in different response structures
            if (data.referralCode) {
              setReferralCode(data.referralCode);
              //console.log("🎉 Successfully set referral code:", data.referralCode);
            } else if (data.referralcode) {
              setReferralCode(data.referralcode);
            } else if (data.referral) {
              setReferralCode(data.referral);
            } else if (data.code) {
              setReferralCode(data.code);
            } else {
              // Generate a referral code from user ID if not provided
              const generatedCode = `MAMA#${id.toString().padStart(3, '0')}`;
              setReferralCode(generatedCode);
              //console.log("⚠️ No referral code in response, generated:", generatedCode);
            }

            // Extract referral stats from profile response
            if (data.totalReferals !== undefined || data.totalReferrals !== undefined) {
              setReferralStats(prev => ({
                ...prev,
                totalReferrals: data.totalReferals || data.totalReferrals || 0,
                earnedAmount: data.totalCashBack || data.earnedAmount || data.cashback || 0,
                profileImage: data.image || data.profileImage || ""
              }));
            }
          }
        } catch (profileError) {
          console.warn("⚠️ Profile API failed, trying referral API:", profileError.message);
        }

        // ✅ FIXED: Correct referral stats API
        try {
          const statsApiUrl = `https://backend.maamaas.com/subscription/api/referral/stats/${id}`;
          //console.log("📊 Fetching referral stats from:", statsApiUrl);

          const statsResponse = await axios.get(statsApiUrl, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'auth-token': token
            },
            timeout: 10000
          });

          if (statsResponse.data) {
            //console.log("✅ Referral Stats Response:", statsResponse.data);
            const statsData = statsResponse.data;
            
            setReferralStats(prev => ({
              ...prev,
              totalReferrals: statsData.totalReferrals || statsData.totalReferals || statsData.referralCount || prev.totalReferrals,
              earnedAmount: statsData.earnedAmount || statsData.totalEarnings || statsData.cashback || statsData.totalCashBack || prev.earnedAmount
            }));
          }
        } catch (statsError) {
          console.warn("⚠️ Referral stats API failed:", statsError.message);
        }

        // ✅ FIXED: Alternative API endpoint for referral data
        try {
          const referralApiUrl = `https://backend.maamaas.com/subscription/api/referral/user/${id}`;
          //console.log("🎯 Trying referral user API:", referralApiUrl);

          const referralResponse = await axios.get(referralApiUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'auth-token': token
            },
            timeout: 10000
          });

          if (referralResponse.data) {
            //console.log("✅ Referral User API Response:", referralResponse.data);
            const referralData = referralResponse.data;
            
            // Update referral code if found
            if (referralData.referralCode && !referralCode) {
              setReferralCode(referralData.referralCode);
            }
          }
        } catch (referralError) {
          console.warn("⚠️ Referral user API failed:", referralError.message);
        }

      } catch (error) {
        console.error("❌ Error fetching referral data:", error);
        setError(error.message);
        
        // Fallback to default values
        setReferralCode("MAMA#409");
        setReferralStats({
          totalReferrals: 0,
          earnedAmount: 0,
          profileImage: ""
        });
      } finally {
        setLoading(false);
      }
    };

    // Call the function
    fetchReferralData();
  }, [userId, authToken]);

  // Generate formatted referral message
  const generateReferralMessage = () => {
    const message = `🚀 Join me on Maamaas App!\n\n✨ Use my referral code: ${referralCode || "MAMA#409"}\n\n📱 Download the app: ${APP_DOWNLOAD_LINK}\n\n🎁 Get amazing rewards when you sign up!\n\n💰 You get: 25% off up to ₹200\n🤝 I get: Rewards too!\n\nDon't miss out!`;
    return message;
  };

  // Generate only the referral link without code
  const generateReferralLink = () => {
    return APP_DOWNLOAD_LINK;
  };

  // Handle copy with formatted text
  const handleCopy = (type = "code") => {
    let textToCopy = "";
    
    if (type === "code") {
      textToCopy = generateReferralMessage();
    } else if (type === "link") {
      textToCopy = generateReferralLink();
    }

    if (!textToCopy) {
      //console.log("No content to copy");
      return;
    }
    
    // Modern clipboard API with fallback
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          
          // Track copy event
          trackReferralShare('copy');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          fallbackCopy(textToCopy);
        });
    } else {
      fallbackCopy(textToCopy);
    }
  };

  // Fallback copy method
  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackReferralShare('copy');
    } catch (err) {
      console.error('Fallback copy failed: ', err);
    }
    
    document.body.removeChild(textArea);
  };

  // Track referral sharing - FIXED API
  const trackReferralShare = async (platform) => {
    try {
      const token = authToken || localStorage.getItem("authToken") || sessionStorage.getItem("token");
      const id = userId || localStorage.getItem("userId") || 15;
      
      if (!token) {
        console.warn("No auth token available for tracking");
        return;
      }

      //console.log(`📊 Tracking share on ${platform} for user ${id}`);

      // Try different API endpoints
      const trackApiUrl = `https://backend.maamaas.com/subscription/api/referral/track`;
      
      const response = await fetch(trackApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'auth-token': token
        },
        body: JSON.stringify({
          userId: id,
          referralCode: referralCode || "MAMA#409",
          platform: platform,
          timestamp: new Date().toISOString(),
          action: 'share'
        })
      });
      
      if (response.ok) {
        //console.log(`✅ Referral share tracked on ${platform}`);
      } else {
        console.warn(`⚠️ Track API returned ${response.status}`);
      }
    } catch (error) {
      console.error(`❌ Error tracking referral share:`, error);
    }
  };

  // Share functions with tracking
  const shareViaWhatsApp = () => {
    const message = generateReferralMessage();
    trackReferralShare('whatsapp');
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaFacebook = () => {
    const message = generateReferralMessage();
    trackReferralShare('facebook');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(APP_DOWNLOAD_LINK)}&quote=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaTelegram = () => {
    const message = generateReferralMessage();
    trackReferralShare('telegram');
    window.open(`https://t.me/share/url?url=${encodeURIComponent(APP_DOWNLOAD_LINK)}&text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = "Join me on Maamaas App! 🎁 Get 25% off";
    const body = generateReferralMessage();
    trackReferralShare('email');
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const shareViaSMS = () => {
    const message = generateReferralMessage();
    trackReferralShare('sms');
    window.open(`sms:?body=${encodeURIComponent(message)}`);
  };

  const shareViaLinkedIn = () => {
    const message = generateReferralMessage();
    trackReferralShare('linkedin');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(APP_DOWNLOAD_LINK)}&summary=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaInstagram = () => {
    const message = generateReferralMessage();
    trackReferralShare('instagram');
    alert("Share this referral code on Instagram:\n\n" + message + "\n\nCopy the text above and share it in your Instagram story or DM!");
  };

  const shareOptions = [
    { name: "WhatsApp", icon: <FaWhatsapp />, color: "#25D366", action: shareViaWhatsApp },
    { name: "Gmail", icon: <SiGmail />, color: "#EA4335", action: shareViaEmail },
    { name: "SMS", icon: <MessageSquare size={20} />, color: "#FF9500", action: shareViaSMS },
    { name: "Telegram", icon: <FaTelegram />, color: "#0088cc", action: shareViaTelegram },
    { name: "Facebook", icon: <FaRegStar />, color: "#1877F2", action: shareViaFacebook },
    { name: "LinkedIn", icon: <FaLinkedin />, color: "#0077B5", action: shareViaLinkedIn },
    { name: "Instagram", icon: <FaInstagram />, color: "#E4405F", action: shareViaInstagram },
  ];

  // Send invitation to selected contact - FIXED API
  const sendInvitation = async (contact) => {
    try {
      const token = authToken || localStorage.getItem("authToken") || localStorage.getItem("token");
      const id = userId || localStorage.getItem("userId") || 15;
      
      if (!token) {
        alert("Please login to send invitations");
        return;
      }

      //console.log(`📤 Sending invitation to ${contact.name} (${contact.number})`);

      // Try different API endpoints for sending invites
      const inviteApiUrl = `https://backend.maamaas.com/subscription/api/referral/invite`;
      
      const response = await fetch(inviteApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'auth-token': token
        },
        body: JSON.stringify({
          referrerId: id,
          referralCode: referralCode || "MAMA#409",
          contactName: contact.name,
          contactPhone: contact.number,
          contactEmail: contact.email || "",
          referralLink: APP_DOWNLOAD_LINK,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        //console.log(`✅ Invitation sent to ${contact.name}`);
        
        // Open SMS with pre-filled message
        const message = generateReferralMessage();
        const smsUrl = `sms:${contact.number}?body=${encodeURIComponent(message)}`;
        window.open(smsUrl, '_blank');
        
        // Update local stats
        setReferralStats(prev => ({
          ...prev,
          totalReferrals: (prev.totalReferrals || 0) + 1
        }));
        
        // Show success message
        alert(`Invitation sent to ${contact.name}!`);
      } else {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error('Failed to send invitation via API');
      }
    } catch (error) {
      console.error('❌ Error sending invitation:', error);
      // Fallback: Open SMS directly even if API fails
      const message = generateReferralMessage();
      const smsUrl = `sms:${contact.number}?body=${encodeURIComponent(message)}`;
      window.open(smsUrl, '_blank');
      alert(`SMS opened for ${contact.name}. Please send it manually.`);
    }
  };

  // Effect for popup
  useEffect(() => {
    document.body.style.overflow = showPopup ? "hidden" : "auto";
  }, [showPopup]);

  // Fetch profile data - Consolidated with main fetch
  useEffect(() => {
    const fetchProfileData = async () => {
      // This is now handled in the main fetchReferralData function
      // Keeping this as backup
    };
    
    if (!referralStats.totalReferrals && !loading) {
      fetchProfileData();
    }
  }, [referralStats.totalReferrals, loading]);

  // Loading state
  if (loading) {
    return (
      <div className="refer-earn-container loading">
        <div className="premium-banner">
          <div className="banner-content">
            
          </div>
        </div>
        
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="refer-earn-container error">
        <div className="premium-banner">
          <div className="banner-content">
           
            <p className="error-message">{error}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="refer-earn-container">
      {/* Premium Header Banner */}
      <div className="premium-banner">
        <div className="banner-content">
          <h1 className="banner-title">Refer your friend & after they join, you get ₹50 off + they get pass @ ₹1</h1>
          <p className="banner-subtitle">
            Invite friends and earn exclusive rewards together!
          </p>
        </div>
        <div className="banner-gradient"></div>
      </div>

      {/* Referral Statistics */}
      <h2 className="section-title">Your Referral Earnings</h2>
       <div className="referral-stats-section">
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{referralStats.totalReferrals}</div>
            <div className="stat-label">Total Referrals</div>
          </div>
                    <div className="stat-card">
            <div className="stat-number">₹{referralStats.earnedAmount}</div>
             <div className="stat-label">Earned</div>
           </div>
          
         </div>
       </div>
        
        {/* Display current referral code */}
        {/* {referralCode && (
          <div className="current-code-display">
            <span className="code-label">Your Code: </span>
            <span className="code-value">{referralCode}</span>
          </div>
        )}
      */}

      {/* How It Works Section */}
      <div className="how-it-works-section">
        <h2 className="section-title">How it works</h2>
        <div className="steps-container">
          <div className="step">
            <div className="step-icon">
              <Share2 size={24} />
            </div>
            <div className="step-content">
              <h3>Share the referral link with your friend</h3>
              <p>Send your unique referral link or code</p>
            </div>
          </div>
          <div className="step">
            <div className="step-icon">
              <Gift size={24} />
            </div>
            <div className="step-content">
              <h3>Friend downloads & uses your code</h3>
              <p>They install Maamaas App and use your referral code</p>
            </div>
          </div>
          <div className="step">
            <div className="step-icon">
              <Users size={24} />
            </div>
            <div className="step-content">
              <h3>You both get rewards!</h3>
              <p>You get 25% off up to ₹200, they get benefits too!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Referral Code Section */}
      <div className="premium-referral-card">
        <div className="card-header">
          <h2>Your Referral Code</h2>
          <div className="premium-badge">Premium</div>
        </div>
        <div className="code-container">
          <div className="code-display">
            <span className="code-text">{referralCode || "MAMA#409"}</span>
          </div>
          <button
            onClick={() => handleCopy("code")}
            disabled={!referralCode}
            className={`copy-button ${copied ? 'copied' : ''}`}
          >
            {copied ? (
              <>
                <span className="check-icon">✓</span>
                Copied!
              </>
            ) : (
              <>
                <Copy size={18} />
                Copy Code & Link
              </>
            )}
          </button>
        </div>
        <p className="copy-note">Copies referral code with download link in formatted text</p>
      </div>

      {/* Share Options */}
      <div className="share-section">
        <div className="section-header">
          <Share2 className="section-icon" />
          <h3>Share via</h3>
        </div>
        <div className="share-grid">
          {shareOptions.map((platform, index) => (
            <button
              key={index}
              className="share-button"
              onClick={platform.action}
              style={{ '--platform-color': platform.color }}
              aria-label={`Share via ${platform.name}`}
            >
              <span className="share-icon">{platform.icon}</span>
              <span className="share-name">{platform.name}</span>
            </button>
          ))}
        </div>
        <button 
          className="whatsapp-share-btn"
          onClick={shareViaWhatsApp}
        >
          <FaWhatsapp size={20} />
          Invite via WhatsApp
        </button>
      </div>

      {/* Share Invite Link Section */}
      {/* <div className="share-invite-section">
        <h3>Share Your Referral Link</h3>
        <p className="link-description">Click "Copy Link" to copy just the app download link</p>
        <div className="invite-link-container">
          <div className="link-display">
            <span className="link-text">{APP_DOWNLOAD_LINK}</span>
          </div>
          <button className="copy-link-btn" onClick={() => handleCopy("link")}>
            <Copy size={16} />
            Copy Link
          </button>
        </div>
      </div> */}

      {/* Benefits Section */}
      <div className="benefits-section">
        <h3>Your Benefits</h3>
        <div className="benefits-list">
          <div className="benefit-item">
            <div className="benefit-icon">🎁</div>
            <div className="benefit-content">
              <h4>25% Discount</h4>
              <p>Get 25% off up to ₹200 on your next order</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">💰</div>
            <div className="benefit-content">
              <h4>₹2000 Bonus</h4>
              <p>Earn ₹2000 after 10 successful referrals</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">👥</div>
            <div className="benefit-content">
              <h4>Friend Benefits</h4>
              <p>Your friend also gets special benefits on their first order</p>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info (only in development) */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="debug-info">
          <details>
            <summary>API Debug Info</summary>
            <pre>
              User ID: {userId || localStorage.getItem("userId") || "Not found"}
              <br />
              Token: {authToken ? "Present" : "Not found"}
              <br />
              Referral Code: {referralCode}
              <br />
              Total Referrals: {referralStats.totalReferrals}
              <br />
              Earned Amount: ₹{referralStats.earnedAmount}
            </pre>
          </details>
        </div>
      )} */}

      {/* Contacts Popup Modal (commented out but kept for reference) */}
      {showPopup && (
        <div className="modal-overlay" onClick={() => setShowPopup(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Contact to Invite</h2>
              <button 
                className="close-button"
                onClick={() => setShowPopup(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="contacts-list">
              {[
                { id: 1, name: "John Doe", number: "+1234567890", email: "john@example.com" },
                { id: 2, name: "Jane Smith", number: "+1987654321", email: "jane@example.com" },
                { id: 3, name: "Alice Brown", number: "+1122334455", email: "alice@example.com" },
              ].map((contact) => (
                <div
                  key={contact.id}
                  className={`contact-item ${
                    selectedContact?.id === contact.id ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedContact(contact)}
                >
                  <div className="contact-avatar">
                    {contact.name.charAt(0)}
                  </div>
                  <div className="contact-info">
                    <div className="contact-name">{contact.name}</div>
                    <div className="contact-number">{contact.number}</div>
                  </div>
                  <button 
                    className="invite-single-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      sendInvitation(contact);
                      setShowPopup(false);
                      setSelectedContact(null);
                    }}
                  >
                    Invite
                  </button>
                </div>
              ))}
            </div>
            {selectedContact && (
              <div className="modal-footer">
                <button
                  className="send-invite-btn"
                  onClick={() => {
                    sendInvitation(selectedContact);
                    setShowPopup(false);
                    setSelectedContact(null);
                  }}
                >
                  Send Invite to {selectedContact.name}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferEarn;





// import React, { useState, useEffect } from "react";
// import "./RefernEarn.css";
// import REferedashbord from '../Profile/REferedashbord.jsx';
// // import Tableordertype from './Tableordertype'
// // import Cateringmainorder from './Cateringmainorder'
// import { useLocation } from "react-router-dom";
// import Referalhistory from "./Referalhistory";
// import RewardHistory from "./RewardHistory";

// /* ---------- Main Company Component ---------- */
// const Company = () => {
//   const location = useLocation();

//   const [activeTab, setActiveTab] = useState(null);
//   const [tabs, setTabs] = useState([]);

//   // Define all available tabs with their submodule mapping
//   const allTabs = [
//     { 
//       id: 1, 
//       name: "Dashboard", 
//       component: () => <REferedashbord title="FOOD & BEVERAGES" />,
//       subModuleKey: "ABOUT_US"
//     },
//     { 
//       id: 2, 
//       name: "Referal History", 
//       component: () => <Referalhistory title="Company Structure" />,
//       subModuleKey: "catring-Orders"
//     },
//     { 
//       id: 3, 
//       name: "Reward-History", 
//       component: () => <RewardHistory title="Company Structure" />,
//       subModuleKey: "Tbale-Orders"
//     },
//   ];

//   useEffect(() => {
//     const userRole = localStorage.getItem("role");
//     const subModules = JSON.parse(localStorage.getItem("subModules") || "[]");

//     let availableTabs = [];

//     if (userRole === "ROLE_EMPLOYEE") {
//       availableTabs = allTabs.filter(tab =>
//         subModules.includes(tab.subModuleKey)
//       );
//       //console.log("Employee - Available subModules:", subModules);
//       //console.log("Employee - Available tabs:", availableTabs);
//     } else {
//       availableTabs = [...allTabs];
//       //console.log("Vendor - Showing all tabs");
//     }

//     setTabs(availableTabs);

//     // PRIORITY 1: Check location state first
//     const stateTab = location.state?.activeTab;
    
//     // PRIORITY 2: Check localStorage
//     const savedTab = localStorage.getItem("activeTab");
    
//     // PRIORITY 3: Use default (first tab)

//     //console.log("STATE TAB:", stateTab);
//     //console.log("SAVED TAB:", savedTab, "Type:", typeof savedTab);
//     //console.log("LOCATION STATE:", location.state);

//     if (availableTabs.length > 0) {
//       // FIRST PRIORITY - location.state (most reliable)
//       if (stateTab !== undefined && stateTab !== null) {
//         //console.log("Setting tab from STATE:", stateTab);
//         setActiveTab(stateTab);
//         // Clean up localStorage if it exists
//         if (savedTab) localStorage.removeItem("activeTab");
//       } 
//       // SECOND PRIORITY - localStorage
//       else if (savedTab !== null && savedTab !== undefined) {
//         const tabNumber = Number(savedTab);
//         //console.log("Setting tab from localStorage:", tabNumber);
//         setActiveTab(tabNumber);
//         localStorage.removeItem("activeTab");
//       } 
//       // THIRD PRIORITY - default first tab
//       else {
//         //console.log("Setting default tab:", availableTabs[0].id);
//         setActiveTab(availableTabs[0].id);
//       }
//     }
//   }, [location]); // Re-run when location changes

//   const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

//   // If no tabs available, show a message
//   if (tabs.length === 0) {
//     return (
//       <div className="company-container">
//         <div style={{ 
//           textAlign: "center", 
//           padding: "50px",
//           color: "#64748b"
//         }}>
//           <h3>No modules available</h3>
//           <p>You don't have access to any modules in this section.</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="company-container">
//       <style>{`
//         /* Default (Mobile first) */
//         .company-scroll-container {
//           position: static;
//           background: #fff;
//         }
      
//         .company-buttons-row {
//           display: flex;
//           flex-wrap: wrap;
//         }
      
//         .company-button {
//           padding: 8px 16px;
//           border: none;
//           background: #f0f0f0;
//           cursor: pointer;
//           border-radius: 6px;
//           white-space: nowrap;
//         }
      
//         .company-button.active {
//           background: #e66d33;
//           color: #fff;
//         }
      
//         /* Desktop / Laptop only */
//         @media (min-width: 769px) {
//           .company-scroll-container {
//             position: sticky;
//             top: 0;
//             z-index: 100;
//             padding: 10px 0;
//           }
//         }
//       `}</style>

//       {/* Tabs */}
//       <div className="company-scroll-container">
//         <div className="company-buttons-row">
//           {tabs.map(tab => (
//             <button
//               key={tab.id}
//               className={`company-button ${activeTab === tab.id ? "active" : ""}`}
//               onClick={() => setActiveTab(tab.id)}
//             >
//               {tab.name}
//             </button>
//           ))}
//         </div>
//       </div>

//       {/* Active Section */}
//       <div className="component-display" style={{height: window.innerHeight < 450 ? "50vh" : 
//         window.innerHeight < 500 ? "55vh" :
//         window.innerHeight < 520 ? "60vh" :
//         window.innerHeight < 540 ? "62vh" :
//         window.innerHeight < 560 ? "64vh" :
//         window.innerHeight < 580 ? "66vh" :
//         window.innerHeight < 600 ? "68vh" :
//         window.innerHeight < 620 ? "69vh" :
//         window.innerHeight < 640 ? "69vh" :
//         window.innerHeight < 660 ? "70vh" :
//         window.innerHeight < 680 ? "71vh" :
//         window.innerHeight < 700 ? "72vh" :
//         window.innerHeight < 720 ? "73vh" :
//         window.innerHeight < 740 ? "77vh" :
//         window.innerHeight < 760 ? "80vh" :
//         window.innerHeight < 780 ? "80vh" :
//         window.innerHeight < 800 ? "80vh" : "80vh"}}>
//         {ActiveComponent && <ActiveComponent />}
//       </div>
//     </div>
//   );
// };

// export default Company;
